import {
  Fieldset,
  ActionIcon,
  Group,
  Button,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  MultiSelect,
  Text,
  Box,
  LoadingOverlay,
} from '@mantine/core';
import {
  IconTrash,
  IconPlus,
} from '@tabler/icons-react';
import { randomId } from '@mantine/hooks';

import { useUpdateMethodCredentials } from '../../hooks/use-update-method-credentials';

import type { TPaymentMethod, TPaymentMethodCredential } from '@/modules/payment-methods/store/types';

type Props = {
  paymentMethod: TPaymentMethod;
  credentials: TPaymentMethodCredential;
  onClose: () => void;
};

export const MethodCredentialsUpdateForm = (props: Props) => {
  const {
    paymentMethod,
    credentials,
    onClose,
  } = props;

  const {
    shops,
    form,
    isLoading,
    handleSubmit,
  } = useUpdateMethodCredentials({ credentials, onClose });

  const validForList = paymentMethod.data?.step1.options.map((option) => option.name);

  if (!validForList) {
    return (
      <Text>Нет данных реквизитов своего платёжного метода</Text>
    );
  }

  if (!shops) {
    return (
      <Text>Загружаем данные по магазинам...</Text>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <TextInput
        label="ID реквизитов"
        disabled
        withAsterisk
        style={{ flex: 1 }}
        key={form.key('credentialsId')}
        {...form.getInputProps('credentialsId')}
      />
      <Fieldset legend="Реквизиты" styles={{ root: { padding: '10px' } }}>
        {form.getValues().fields.map((_, index) => (
          <Group key={form.key(`fields.${index}`)} mt="xs" gap="xs">
            <TextInput
              placeholder="Заголовок"
              withAsterisk
              style={{ flex: 1 }}
              key={form.key(`fields.${index}.name`)}
              {...form.getInputProps(`fields.${index}.name`)}
            />
            <Textarea
              placeholder="Содержание"
              withAsterisk
              autosize
              style={{ flex: 1 }}
              key={form.key(`fields.${index}.value`)}
              {...form.getInputProps(`fields.${index}.value`)}
            />
            <ActionIcon color="red" onClick={() => form.removeListItem('fields', index)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ))}

        {form.errors.fields && (
          <Text c="red" size="sm" mt="sm">
            {form.errors.fields}
          </Text>
        )}

        <Group justify="center" mt="md">
          <Button
            variant="default"
            size="xs"
            leftSection={<IconPlus size={12} />}
            onClick={() => {
              form.insertListItem('fields', { name: '', value: '', key: randomId() });
              form.clearFieldError('fields');
            }}
          >
            Добавить реквизит
          </Button>
        </Group>
      </Fieldset>

      <Fieldset legend="Опции" styles={{ root: { padding: '10px' } }}>
        <MultiSelect
          label="Способы оплаты (множественный выбор)"
          // placeholder="Выберите способы оплаты"
          searchable
          nothingFoundMessage="Ничего не найдено..."
          withAsterisk
          data={validForList}
          key={form.key('validFor')}
          {...form.getInputProps('validFor')}
        />
        <NumberInput
          label="Лимит пополнения"
          key={form.key('limit')}
          {...form.getInputProps('limit')}
        />
        <Box pos="relative">
          <LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
          <Select
            label="ID магазина"
            placeholder=""
            data={shops.map((shop) => ({ value: shop.id.toString(), label: `${shop.id} - ${shop.name}` }))}
            key={form.key('shopId')}
            {...form.getInputProps('shopId')}
          />
        </Box>
        <TextInput
          label="Для клиента с ID"
          key={form.key('forPayerId')}
          {...form.getInputProps('forPayerId')}
        />
        <TextInput
          label="Исключить ID клиента"
          key={form.key('exceptPayerId')}
          {...form.getInputProps('exceptPayerId')}
        />
      </Fieldset>

      <Group justify="flex-end" mt="30">
        <Button variant="outline" onClick={() => onClose()}>
          Отменить
        </Button>
        <Button type="submit">
          Редактировать
        </Button>
      </Group>
    </form>
  );
};
